import React from 'react';
import { useDeviceSize } from 'common/lib/hooks/useDeviceSize';
import PropTypes from 'prop-types';
import CompanyProfile from '../components/companyProfile';
import AdDescription from '../components/adDescription';
import AdHeading from '../components/adHeading';
import ContentMap from '../components/map';
import EndNotes from '../components/endNotes';
import ImageCarousel from '../components/imageCarousel';
import ImproveVisibilityPanel from '../components/improveVisibility';
import InterestListButton from '../components/interestListButton';
import ProgressBar from '../components/progressBar';
import NeighbourhoodProfile from '../components/neighbourhood';
import CollapsableText from '../components/collapsableText';
import RedirectBanner from '../components/redirectBanner';
import ExternalContent from '../components/externalContent';
import UsefulLinks from '../components/usefulLinks';
import SharingOptions from '../components/sharingOptions';
import KeyInfo from '../components/keyInfo';
import SearchHeader from '../components/searchHeader';

const AppKfs = ({ data }) => {
    const {
        createdBy,
        latitude,
        longitude,
        streetAddress,
        localAreaName,
        postNumber,
        postArea,
        description,
        userId,
        finnkode,
        orgId,
        adStatus,
        layoutPath,
        finnkodeNextAd,
        hasActiveLinkedPts,
        podletPath,
        heading,
        projectUrl,
        projectName,
        lastUpdatedAt,
        externalAdId,
        adStageStatusOne,
        adStageStatusTwo,
        adStageStatusThree,
        adStageStatusFour,
        moreInfoLinks,
        adType,
        leisure,
        communicationEmail,
    } = data;

    const { isMobile, isDesktop } = useDeviceSize();

    const mobileView = (
        <>
            <SharingOptions finnkode={finnkode} heading={heading} />

            <AdHeading
                mainTitle={projectName}
                secondaryTitle={heading}
                postArea={postArea}
                adStatus={adStatus}
                postNumber={postNumber}
                streetAddress={streetAddress}
                localAreaName={localAreaName}
            />

            <ProgressBar
                adStageStatusOne={adStageStatusOne}
                adStageStatusTwo={adStageStatusTwo}
                adStageStatusThree={adStageStatusThree}
                adStageStatusFour={adStageStatusFour}
            />

            <KeyInfo data={data} />

            <InterestListButton adStatus={adStatus} communicationEmail={communicationEmail} />

            <CollapsableText>
                <AdDescription description={description} />
            </CollapsableText>

            <UsefulLinks projectUrl={projectUrl} moreInfoLinks={moreInfoLinks} orgId={orgId} isLeisure={leisure} />
        </>
    );

    const desktopView = (
        <div className="grid grid-cols-3 gap-32">
            <div className="col-span-2">
                <SharingOptions finnkode={finnkode} heading={heading} />

                <AdHeading
                    mainTitle={projectName}
                    secondaryTitle={heading}
                    adStatus={adStatus}
                    postNumber={postNumber}
                    postArea={postArea}
                    streetAddress={streetAddress}
                    localAreaName={localAreaName}
                />
                <KeyInfo data={data} />

                <CollapsableText>
                    <AdDescription description={description} />
                </CollapsableText>
            </div>

            <div className="col-span-1">
                <div className="top-64 sticky mt-16" style={{ zIndex: 2 }}>
                    {/* z-index is added here to override the default z-index of position sticky. This is 2 here because the FINN header has index 3, and the side column should not overlay the header */}
                    <InterestListButton adStatus={adStatus} communicationEmail={communicationEmail} />
                    <UsefulLinks
                        projectUrl={projectUrl}
                        moreInfoLinks={moreInfoLinks}
                        orgId={orgId}
                        isLeisure={leisure}
                    />
                </div>
            </div>
        </div>
    );

    return (
        <div className="page-container">
            <SearchHeader finnkode={finnkode} adType={adType} />

            {isDesktop && (
                <ProgressBar
                    adStageStatusOne={adStageStatusOne}
                    adStageStatusTwo={adStageStatusTwo}
                    adStageStatusThree={adStageStatusThree}
                    adStageStatusFour={adStageStatusFour}
                />
            )}
            <ImageCarousel />

            {isDesktop && userId === createdBy.toString() && (
                <ImproveVisibilityPanel finnkode={finnkode} orgId={orgId} adStatus={adStatus} layoutPath={layoutPath} />
            )}
            {hasActiveLinkedPts && <RedirectBanner finnkodeNextAd={finnkodeNextAd} podletPath={podletPath} />}

            <CompanyProfile expandable data={data} />

            {isDesktop ? desktopView : mobileView}

            <ContentMap
                mobile={isMobile}
                longitude={longitude}
                latitude={latitude}
                postNumber={postNumber}
                postArea={postArea}
                streetAddress={streetAddress}
            />
            <NeighbourhoodProfile finnkode={finnkode} />

            <ExternalContent finnkode={finnkode} />

            <CompanyProfile data={data} />

            <EndNotes lastUpdatedAt={lastUpdatedAt} finnkode={finnkode} externalAdId={externalAdId} />
        </div>
    );
};

AppKfs.propTypes = {
    data: PropTypes.object.isRequired,
};

export default AppKfs;
